import type { RiskType, UpcomingPolicyStep } from "@unbox/api";
import { AlertTriangle } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Table } from "@/components/application/table/table";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { cx } from "@/utils/cx";
import { formatDurationUntil } from "@/utils/date-conversion";
import "../../../table-styles.css";
import { useSeenRows } from "@/components/application/table/use-seen-rows";
import { FLAGGED_VIDEOS_TABLE_KEY } from "@/pages/safety-beta/flagged/components/VideoProgressRow";
import type { ActivityItem } from "../../components/ActivityFeed";
import { VideoProgressRow } from "../../components/VideoProgressRow";

export type InProgressVideo = {
  id: string;
  title: string;
  creator: string;
  creatorId?: string;
  views: string;
  postedAgo: string;
  postedAtDate: string;
  topFlags: { name: string; severity: "critical" | "high" | "medium" | "low" } | null;
  flagCount: number;
  urlFirstFrame?: string | null;
  isInAdCampaign?: boolean;
  policy: string;
  nextScanDueAt?: string | Date | null;
  failed?: boolean;
  isQuarantined?: boolean;
  riskDetails: {
    name: string;
    severity: "critical" | "high" | "medium" | "low";
    description?: string;
    evidenceType: RiskType;
    confirmedBy?: string;
    confirmedAt?: string;
    evidence: { ts?: string; text: string }[];
  }[];
  activity: ActivityItem[];
  upcomingSteps: UpcomingPolicyStep[];
};

const PolicyStatus = ({ video }: { video: InProgressVideo }) => {
  const reScanIn = video.nextScanDueAt ? formatDurationUntil(video.nextScanDueAt) : null;

  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <Tooltip title={video.policy}>
        <TooltipTrigger className="inline-flex w-full min-w-0 justify-start">
          <Badge size="sm" color="gray" className="inline-block w-full truncate align-middle">
            {video.policy}
          </Badge>
        </TooltipTrigger>
      </Tooltip>
      {reScanIn && !video.failed && <span className="text-xs text-quaternary">Re-scan in {reScanIn}</span>}
      {video.failed && (
        <span className="inline-flex items-center gap-1 text-xs text-red-600">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          Policy run failed
        </span>
      )}
    </div>
  );
};

type VideoProgressTableProps = {
  videos: InProgressVideo[];
  total: number;
  selectedId: string;
  onSelect: (id: string) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  // True while the detail panel is closed — widens the Risks/Policy columns (and shrinks
  // Views) to use the reclaimed space instead of leaving it all in Views.
  fullWidth?: boolean;
};

export const VideoProgressTable = ({
  videos,
  total,
  selectedId,
  onSelect,
  hasActiveFilters,
  onClearFilters,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  fullWidth,
}: VideoProgressTableProps) => {
  const { markSeen, seenCount } = useSeenRows(FLAGGED_VIDEOS_TABLE_KEY);
  const { scrollRef, onScroll } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
    itemCount: videos.length,
  });

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className={cx(
        fullWidth ? "w-full" : "w-[calc(57%-0.5rem)]",
        "shrink-0 min-w-[520px] border border-border-primary rounded-md overflow-y-auto bg-white h-full",
      )}
    >
      <Table
        size="sm"
        aria-label="With Creator to Action videos"
        onRowAction={(key) => {
          markSeen(key as string);
          onSelect(key as string);
        }}
        className="table-fixed"
      >
        <Table.Header className="bg-white">
          <Table.Head
            id="video"
            label={`${total} Videos`}
            isRowHeader
            className={cx(
              "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-progress-table-col-video-full" : "flagged-progress-table-col-video-compact",
            )}
          />
          <Table.Head
            id="views"
            label="Views"
            className={cx(
              "px-[12px] py-[6px] [&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-progress-table-col-views-full" : "flagged-progress-table-col-views-compact",
            )}
          />
          <Table.Head
            id="risks"
            label="Risks"
            allowsSorting
            className={cx(
              "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-progress-table-col-risks-full" : "flagged-progress-table-col-risks-compact",
            )}
          />
          <Table.Head
            id="policy"
            label="Policy & Status"
            className={cx(
              "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary pl-[12px]",
              fullWidth ? "flagged-progress-table-col-status-full" : "flagged-progress-table-col-status-compact",
            )}
          />
        </Table.Header>
        <Table.Body
          items={videos}
          dependencies={[seenCount]}
          renderEmptyState={() => (
            <div className="h-full flex items-center justify-center">
              <UnboxTableEmptyStateContent
                rows={[]}
                hasActiveFilters={hasActiveFilters}
                filteredEmptyState={{ onClearFilters }}
              />
            </div>
          )}
        >
          {(video) => (
            <VideoProgressRow
              id={video.id}
              title={video.title}
              creator={video.creator}
              views={video.views}
              postedAgo={video.postedAgo}
              topFlags={video.topFlags}
              flagCount={video.flagCount}
              urlFirstFrame={video.urlFirstFrame}
              isInAdCampaign={video.isInAdCampaign}
              selected={video.id === selectedId}
              statusContent={<PolicyStatus video={video} />}
              fullWidth={fullWidth}
            />
          )}
        </Table.Body>
      </Table>
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-3">
          <LoadingIndicator size="xs" />
        </div>
      )}
    </div>
  );
};
